import { Box, Icon, Spinner, Tooltip, usePrefersReducedMotion } from '@chakra-ui/react'
import { FiAlertCircle } from 'react-icons/fi'
import { useAppTranslation } from '@/contexts/LanguageContext'
import { useAppSelector } from '@/store'
import { resolveProcessingState } from '@/store/slices/fileProcessingSlice'
import { FileItem } from '@/types'

interface FileProcessingIndicatorProps {
  file: FileItem
  /** Set when polling gave up on this screen, so the spinner stops implying imminent completion. */
  isStalled?: boolean
}

/**
 * Marks a document that is still being processed after its upload finished.
 *
 * A bare spinner rather than a labelled badge: this sits in a file name cell that already
 * carries a name, a type icon and a tag icon, and it appears on every row of a fresh
 * upload. Anything with a background and a word in it reads as a second column.
 *
 * Renders nothing once a file is usable, so a settled folder looks settled.
 */
export default function FileProcessingIndicator({
  file,
  isStalled = false,
}: FileProcessingIndicatorProps) {
  const { t } = useAppTranslation('files')
  const prefersReducedMotion = usePrefersReducedMotion()

  // The poll writes here; the list payload is the fallback until the first tick lands.
  const fileProcessing = useAppSelector((state) => state.fileProcessing.byId)
  const state = resolveProcessingState(file, fileProcessing)

  // `partial` means the text came through but an enrichment step did not. The document
  // opens and reads fine, so it gets no marker — only work in flight and outright
  // failure are worth interrupting a scan of the list for.
  if (!state || state === 'ready' || state === 'partial') return null

  if (state === 'failed') {
    return (
      <Tooltip label={t('fileStatus.failedTooltip')} hasArrow placement="top" openDelay={300}>
        <Box as="span" display="inline-flex" flexShrink={0} lineHeight={0}>
          <Icon
            as={FiAlertCircle}
            boxSize={3.5}
            color="red.500"
            role="img"
            aria-label={t('fileStatus.failed')}
          />
        </Box>
      </Tooltip>
    )
  }

  const tooltip = isStalled
    ? t('fileStatus.takingLongerTooltip')
    : t('fileStatus.processingTooltip')

  return (
    <Tooltip label={tooltip} hasArrow placement="top" openDelay={300}>
      <Box as="span" display="inline-flex" flexShrink={0} lineHeight={0}>
        {prefersReducedMotion || isStalled ? (
          // A spinner that has stopped making progress is a lie; a static dot still marks
          // the row as unfinished without promising it is about to resolve.
          <Box
            as="span"
            boxSize={2}
            borderRadius="full"
            bg={isStalled ? 'gray.400' : 'blue.400'}
            role="status"
            aria-label={isStalled ? t('fileStatus.takingLonger') : t('fileStatus.processing')}
          />
        ) : (
          <Spinner
            size="xs"
            speed="0.8s"
            thickness="2px"
            color="blue.400"
            emptyColor="blackAlpha.100"
            role="status"
            aria-label={t('fileStatus.processing')}
          />
        )}
      </Box>
    </Tooltip>
  )
}
